import AdminHeader from "components/Headers/AdminHeader";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  UncontrolledTooltip,
  Input,
} from "reactstrap";
import Select from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "constants/CommonConstants";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { LEVEL_OPTIONS, TAG_OPTIONS } from "components/constant";

const questionBankList = [
  {
    id: 1,
    questionBankName: "Electrical Safety – Level 1",
    level: "Beginner",
    tag: "Electrical",
  },
  {
    id: 2,
    questionBankName: "Power Systems – Basics",
    level: "Intermediate",
    tag: "Electrical",
  },
  {
    id: 3,
    questionBankName: "Industrial Wiring – Advanced",
    level: "Advanced",
    tag: "Electrical",
  },
  {
    id: 4,
    questionBankName: "Transformer Maintenance",
    level: "Intermediate",
    tag: "Electrical",
  },
  {
    id: 5,
    questionBankName: "Circuit Fundamentals",
    level: "Beginner",
    tag: "Electronics",
  },
  {
    id: 6,
    questionBankName: "Digital Electronics",
    level: "Intermediate",
    tag: "Electronics",
  },
  {
    id: 7,
    questionBankName: "Civil Engineering Basics",
    level: "Beginner",
    tag: "Civil",
  },
  {
    id: 8,
    questionBankName: "Structural Analysis",
    level: "Advanced",
    tag: "Civil",
  },
  {
    id: 9,
    questionBankName: "Mechanical Systems",
    level: "Intermediate",
    tag: "Mechanical",
  },
];

const QuestionBankList = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    searchQuestionBank: "",
    searchLevel: "",
    searchTag: "",
  });

  const filteredData = questionBankList.filter((item) => {
    const matchesQuestionBank =
      !filterState.searchQuestionBank ||
      item.questionBankName
        .toLowerCase()
        .includes(filterState.searchQuestionBank.toLowerCase());

    const matchesLevel =
      !filterState.searchLevel ||
      item.level.toLowerCase().includes(filterState.searchLevel.toLowerCase());

    const matchesTag =
      !filterState.searchTag ||
      item.tag.toLowerCase().includes(filterState.searchTag.toLowerCase());

    return matchesQuestionBank && matchesLevel && matchesTag;
  });

  const formattedData = filteredData.map((item, index) => ({
    ...item,
    srNo: (pageNum - 1) * DEFAULT_RECORDS_PER_PAGE + index + 1,
  }));

  const handleOnRowClick = (rowData) => {
    const encodedId = urlEncodedId(rowData.id); //rowData.ID
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/question-bank/${encodedId}`
        : `/admin/question-bank/${encodedId}`,
    );
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
    },
    {
      Header: "Question Bank Name",
      accessor: "questionBankName",
    },
    {
      Header: "Level",
      accessor: "level",
    },
    {
      Header: "Tag",
      accessor: "tag",
    },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const id = row.original.id;

        return (
          <div className="d-flex align-items-center gap-2">
            {/* View */}
            <Button
              className="btn-link p-0"
              id={`view${id}`}
              size="sm"
              onClick={() =>
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/question-bank/${id}`
                    : `/admin/question-bank/${id}`,
                )
              }
            >
              <i className="fas fa-eye" />
            </Button>
            <UncontrolledTooltip target={`view${id}`}>View</UncontrolledTooltip>

            {/* Edit */}
            <Button
              className="btn-link p-0"
              id={`edit${id}`}
              size="sm"
              onClick={() =>
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/question-bank/${id}`
                    : `/admin/question-bank/${id}`,
                  {
                    state: { mode: "edit", questionBank: row.original },
                  }
                )
              }
            >
              <i className="fas fa-edit" />
            </Button>
            <UncontrolledTooltip target={`edit${id}`}>Edit</UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col>
            <Card>
              {/* HEADER */}
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Question Banks</h3>

                <Button
                  className="btn-deep-blue"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? "/super-admin/question-bank/add"
                        : "/admin/question-bank/add",
                      {
                        state: { mode: "add" },
                      }
                    )
                  }
                >
                  <i className="fa fa-plus mr-1" />
                  Question Bank
                </Button>
              </CardHeader>

              {/* BODY */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by question bank name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchQuestionBank}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchQuestionBank: e.target.value,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      styles={fixedWidthCustomStyle()}
                      options={LEVEL_OPTIONS}
                      placeholder="Select Level"
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchLevel: e.target.value,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      styles={fixedWidthCustomStyle()}
                      options={TAG_OPTIONS}
                      placeholder="Select Tag"
                      value={filterState.searchTag}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchTag: e.target.value,
                        }))
                      }
                    />

                    <Button
                      color="default"
                      // className="d-flex align-items-center btn-deep-blue mb-0"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>
                  </div>
                </div>

                <div className="mb-3">
                  <span className="total-records">
                    Total Records : {filteredData.length}
                  </span>
                </div>

                <CommonTable
                  columns={columns}
                  data={formattedData.slice(
                    (pageNum - 1) * DEFAULT_RECORDS_PER_PAGE,
                    pageNum * DEFAULT_RECORDS_PER_PAGE,
                  )}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                  totalPages={Math.ceil(
                    filteredData.length / DEFAULT_RECORDS_PER_PAGE,
                  )}
                  showPaginationBottom
                  rowClick={handleOnRowClick}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default QuestionBankList;
